/// <reference types="@types/googlemaps" />

import {ElementRef, Inject, Injectable, PLATFORM_ID} from '@angular/core';
import {isPlatformBrowser} from '@angular/common';
import {LeadFormGooglePlacesAutocomplete} from '../models/google-places-autocomplete.model';
import {LeadFormModel} from '../cta-form/lead-form/lead-form.model';
import {Region} from '@spartacus/core';
import {Observable, Subscription} from 'rxjs';
import {take} from 'rxjs/operators';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import PlaceResult = google.maps.places.PlaceResult;

@UntilDestroy()
@Injectable({
  providedIn: 'root'
})
export class GoogleMapsService {

  public gmAutocomplete: LeadFormGooglePlacesAutocomplete;
  public regions: Region[];
  public lastCompanyListener: google.maps.MapsEventListener;
  public lastAddressListener: google.maps.MapsEventListener;
  private regionsSubscription: Subscription = Subscription.EMPTY;
  private pacPlaceChangedEventName: string = 'place_changed';

  constructor(
    @Inject(PLATFORM_ID) private platformId: object
  ) {
  }

  /**
   * Attaches Google Maps Autocomplete functionality to the Company Name and Street address input fields.
   *
   * @param companyNameRef Reference to the Company Name ds-input element.
   * @param streetAddressInputRef Reference to the Street Address ds-input element.
   * @param regions$: Observable that returns an Array of regions
   * @param leadFormModel Form Data model
   */
  attachGoogleMapsAutocomplete(companyNameRef: ElementRef, streetAddressInputRef: ElementRef,
                               regions$: Observable<Region[]>, leadFormModel: LeadFormModel) {

    if (isPlatformBrowser(this.platformId)) {
      this.gmAutocomplete = this.buildAutoComplete(leadFormModel.addressesMap.LTAC.country.isocode,
        companyNameRef, streetAddressInputRef);

      this.regionsSubscription.unsubscribe();
      this.regionsSubscription = regions$.pipe(take(1), untilDestroyed(this)).subscribe((regions) => {
        // regions can be changed by newAutocompleteCountry as well
        this.regions = regions;
        this.addInputListeners(companyNameRef, streetAddressInputRef, leadFormModel);
      });
    }
  }

  /**
   * Adds input listeners for the google lookup to the address and company name fields.
   *
   * @param companyNameRef Reference to the Company Name input element.
   * @param streetAddressInputRef Reference to the Street Address input element.
   * @param leadFormModel Form Data model
   */
  addInputListeners(companyNameRef: ElementRef, streetAddressInputRef: ElementRef, leadFormModel: LeadFormModel): void {
    this.removeInputListeners();
    this.lastCompanyListener = google.maps.event.addListener(this.gmAutocomplete.company.pac, this.pacPlaceChangedEventName, () => {
      this.fillGoogleAddress(companyNameRef, leadFormModel, true);
    });
    this.lastAddressListener = google.maps.event.addListener(this.gmAutocomplete.address.pac, this.pacPlaceChangedEventName, () => {
      this.fillGoogleAddress(streetAddressInputRef, leadFormModel, false);
    });
  }

  /**
   * Removes the google event listeners set by addInputListeners.
   */
  removeInputListeners() {
    if (this.lastCompanyListener) {
      this.lastCompanyListener.remove();
      this.lastCompanyListener = null;
    }
    if (this.lastAddressListener) {
      this.lastAddressListener.remove();
      this.lastAddressListener = null;
    }
  }

  /**
   * Adjusts google places autocomplete country restrictions to new country.
   */
  newAutocompleteCountry(leadFormModel: LeadFormModel, regions: Region[]): void {
    if (isPlatformBrowser(this.platformId)) {
      this.gmAutocomplete.company.pac.setComponentRestrictions({country: leadFormModel.addressesMap.LTAC.country.isocode});
      this.gmAutocomplete.address.pac.setComponentRestrictions({country: leadFormModel.addressesMap.LTAC.country.isocode});
      this.regions = regions;
    }
  }

  /**
   * Builds the Google Maps autocomplete model
   *
   * @param currentCountryIsoCode Iso code of the country currently selected.
   * @param companyNameRef Reference to the Company Name input element.
   * @param streetAddressInputRef Reference to the Street Address ds-input element.
   */
  buildAutoComplete(currentCountryIsoCode: string,
                    companyNameRef: ElementRef,
                    streetAddressInputRef: ElementRef): LeadFormGooglePlacesAutocomplete {
    const gmAutocomplete: LeadFormGooglePlacesAutocomplete = {
      company: {
        inputElement: companyNameRef.nativeElement.querySelector('input'),
        options: {
          componentRestrictions: {
            country: currentCountryIsoCode,
          },
          types: ['establishment'],
        },
        pac: undefined,
      },
      address: {
        inputElement: streetAddressInputRef.nativeElement.querySelector('input'),
        options: {
          componentRestrictions: {
            country: currentCountryIsoCode,
          },
          types: ['address'],
        },
        pac: undefined,
      },
    };

    gmAutocomplete.company.pac = new google.maps.places.Autocomplete(
      gmAutocomplete.company.inputElement, gmAutocomplete.company.options
    );
    gmAutocomplete.address.pac = new google.maps.places.Autocomplete(
      gmAutocomplete.address.inputElement, gmAutocomplete.address.options
    );

    gmAutocomplete.company.inputElement.placeholder = '';
    gmAutocomplete.address.inputElement.placeholder = '';

    return gmAutocomplete;
  }

  /**
   * Gets executed on `place_changed` event
   * Processes google places provided location data
   * region data is mapped via full name provided by google, since we do not support ISO 3166-2 standard
   * in case data can not be mapped or is missing, affected input field will be cleared
   * The global variable "this.regions" is used in this method.
   *
   * @param inputRef Reference to the Company- or Address Name input element.
   * @param leadFormModel Form Data model
   * @param isCompany: boolean which defines if company autocomplete or address autocomplete was triggered
   */
  fillGoogleAddress(inputRef: ElementRef, leadFormModel: LeadFormModel, isCompany: boolean): void {
    const place: PlaceResult = this.getPlace(isCompany);
    if (!place || !place.address_components) {
      return;
    }

    leadFormModel.addressesMap.LTAC.line1 = this.getPlaceStreet(place);

    if (isCompany) {
      leadFormModel.addressesMap.LTAC.companyName = place.name ? place.name : undefined;
      // Needed to make sure only the street name or company name is displayed, instead of the entire address.
      inputRef.nativeElement.querySelector('input').value = leadFormModel.addressesMap.LTAC.companyName;
    } else {
      inputRef.nativeElement.querySelector('input').value = leadFormModel.addressesMap.LTAC.line1;
    }

    leadFormModel.addressesMap.LTAC.town = this.getPlaceTown(place);
    leadFormModel.addressesMap.LTAC.postalCode = this.getPlacePostalCode(place);

    const placeRegion: Region = this.getRegion(this.regions, this.fetchAddressType('administrative_area_level_1', place));

    leadFormModel.addressesMap.LTAC.region = placeRegion;
    leadFormModel.addressesMap.LTAC.regionIsocodeArray = placeRegion ? [placeRegion.isocode] : [undefined];
  }

  fetchAddressType(expectedType: string, place: any, shortName?: boolean): string {
    const typeData: any = place?.address_components?.find((component) => component.types?.find((addressType) => addressType.toLowerCase() === expectedType.toLowerCase()));
    if (!typeData) {
      return '';
    }
    return shortName ? typeData.short_name : typeData.long_name;
  }

  /**
   * Retrieves the current region from the Regions array by the regionId
   *
   * @param regions Array containing all regions
   * @param regionId Identifier of the region
   */
  getRegion(regions: Region[], regionId: string): any {
    if (!regions) {
      return undefined;
    }
    return regions.find((region) => region.name.toLowerCase() === regionId.toLowerCase());
  }

  /**
   * Gets the place from the gmAutocomplete element either for the company or the or address.
   *
   * @param isCompany set to true if the place of the country should be returned.
   */
  getPlace(isCompany: boolean): PlaceResult {
    if (isCompany) {
      return this.gmAutocomplete.company.pac.getPlace();
    } else {
      return this.gmAutocomplete.address.pac.getPlace();
    }
  }

  getPlaceStreet(place: PlaceResult): string {
    const formattedAddressArray: string[] = place?.formatted_address?.split(',');
    return formattedAddressArray? formattedAddressArray[0] : '';
  }

  getPlaceTown(place: PlaceResult): string {
    return this.fetchAddressType('locality', place) || this.fetchAddressType('sublocality', place);
  }

  getPlacePostalCode(place: PlaceResult): string {
    return this.fetchAddressType('postal_code', place);
  }
}
